import { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { TodosContext } from "../../../context";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import Loader from "../../../components/atoms/Loader/Loader";
import DetailsSection from "../../../components/templates/DetailsSection/DetailsSection";
import { ContentDetail } from "../../../components/templates/DetailsSection/types";
import { OrganisationService } from "../service/organisationService";
import { IOrganisation } from "../models/IOrganisation";
import { getCountryName } from "../constants";

const BUTTONS = [{ name: "Back", className: "cancelBtn" }];

const CONTENT_DETAILS: ContentDetail[] = [
  { label: "Code", name: "code" },
  { label: "Subscription Plan", name: "subscriptionPlan" },
  { label: "Country", name: "country" },
  { label: "Primary Contact Name", name: "primaryContactName" },
  { label: "Primary Contact Email", name: "primaryContactEmail" },
  { label: "Primary Contact Phone", name: "primaryContactPhone" },
  { label: "Created At", name: "createdAt" },
  { label: "Edited At", name: "editedAt" },
  { label: "Note", name: "note" },
];

// Shapes an organisation into the flat fields DetailsSection renders.
const toDetailsData = (organisation: IOrganisation): Record<string, string> => ({
  code: organisation.code,
  subscriptionPlan: organisation.subscriptionPlan?.planName ?? "",
  country: getCountryName(organisation.countryId),
  primaryContactName:
    `${organisation.primaryContactFirstName} ${organisation.primaryContactLastName}`.trim(),
  primaryContactEmail: organisation.primaryContactEmail,
  primaryContactPhone: organisation.primaryContactPhone,
  createdAt: organisation.createdAt ?? "",
  editedAt: organisation.editedAt ?? "",
  note: organisation.note ?? "",
});

const OrganisationDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addAlert } = useContext(TodosContext);
  const [organisation, setOrganisation] = useState<IOrganisation>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let isCancelled = false;
    setIsLoading(true);
    OrganisationService.getOrganisation(id)
      .then((response) => {
        if (!isCancelled) setOrganisation(response);
      })
      .catch(() => {
        if (!isCancelled) addAlert("Failed to load organisation", "error", true);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const detailsData = organisation ? toDetailsData(organisation) : undefined;

  const detailsValueGenerator = (item: ContentDetail) => detailsData?.[item.name];

  const handleMainButtonClick = (buttonType: string) => {
    if (buttonType === "Back") navigate("/organisations");
  };

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline name="Organisation details" />
      {isLoading ? (
        <Loader />
      ) : (
        <DetailsSection
          BUTTONS={BUTTONS}
          DetailsValueGenerator={detailsValueGenerator}
          contentDetails={CONTENT_DETAILS}
          handleMainButtonClick={handleMainButtonClick}
          title={organisation?.name ?? ""}
        />
      )}
    </Container>
  );
};

export default OrganisationDetails;
